import * as React from 'react'
import axios from 'axios'

// Intents are slow, so they are created ahead of Pay, one per payment method,
// keyed on everything that moves the amount: a changed plan, country, VAT
// number or coupon discards them (the server refuses a mismatch anyway).
export function usePaymentIntents({ selectedPlan, details }) {
    const intentsRef = React.useRef({ key: null, byMethod: {} })
    const [renewals, setRenewals] = React.useState(0)
    const intentKey = [
        selectedPlan && selectedPlan.planId,
        details.country,
        details.vatNumber,
        details.coupon,
        renewals,
    ].join('|')

    const requestIntent = React.useCallback(
        (paymentMethodType = 'card') => {
            if (!selectedPlan || !details.country) return null
            if (intentsRef.current.key !== intentKey)
                intentsRef.current = { key: intentKey, byMethod: {} }
            const cached = intentsRef.current.byMethod[paymentMethodType]
            if (cached) return cached
            const promise = axios
                .post('/billing/checkout/start', {
                    plan: selectedPlan.planId,
                    country: details.country,
                    company: details.company || undefined,
                    vatNumber: details.vatNumber || undefined,
                    coupon: details.coupon || undefined,
                    paymentMethodType,
                })
                .then(response => response.data)
            // The stored promise is awaited on Pay; this copy keeps a background
            // failure from surfacing as an unhandled rejection.
            promise.catch(() => {})
            intentsRef.current.byMethod[paymentMethodType] = promise
            return promise
        },
        [selectedPlan, details, intentKey]
    )

    // Only the card re-creates its intent after a failure; a wallet button
    // keeps the one it was mounted with and retries on it.
    const discardCardIntent = () => {
        delete intentsRef.current.byMethod.card
    }

    // A wallet intent authorised for a purchase that then failed cannot pay
    // again: Chargebee refuses a second confirm. A new key gives every method
    // a fresh intent and remounts the wallet buttons, as a price change does.
    const renew = () => setRenewals(count => count + 1)

    return { intentKey, requestIntent, discardCardIntent, renew }
}
